import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ConfirmModal from "../components/modals/ConfirmModal";
import ModalCloseX from "../components/common/ModalCloseX";
import { useAuth } from "../context/AuthContext";
import {
  createGoogleDriveAuthUrl,
  disconnectGoogleDrive,
  getGoogleDriveStatus,
  setGoogleDriveEnabled,
  testGoogleDriveUpload,
} from "../api/googleDriveIntegration";
import "./OnlineBookingSectionCompact.css";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.detail || error?.message || fallback;

export default function GoogleDriveIntegration() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const location = useLocation();
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  const connectedFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("connected") === "1";
  }, [location.search]);
  const callbackErrorFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("error") || "";
  }, [location.search]);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getGoogleDriveStatus(token);
      setStatusData(data || null);
    } catch (err) {
      setStatusData(null);
      setError(getErrorMessage(err, "Unable to load Google Drive status."));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleConnect = async () => {
    setBusyAction("connect");
    setError("");
    setNotice("");
    try {
      const data = await createGoogleDriveAuthUrl(token);
      const authUrl = data?.auth_url;
      if (!authUrl) {
        throw new Error("Missing authorization URL.");
      }
      window.location.assign(authUrl);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to start Google Drive connection."));
      setBusyAction("");
    }
  };

  const handleToggleEnabled = async (enabled) => {
    setBusyAction("toggle");
    setError("");
    setNotice("");
    try {
      await setGoogleDriveEnabled(enabled, token);
      await loadStatus();
      setNotice(enabled ? "Google Drive storage enabled." : "Google Drive storage disabled.");
    } catch (err) {
      setError(getErrorMessage(err, "Unable to update Google Drive setting."));
    } finally {
      setBusyAction("");
    }
  };

  const handleTestUpload = async () => {
    setBusyAction("test");
    setError("");
    setNotice("");
    try {
      const result = await testGoogleDriveUpload(token);
      setNotice(
        result?.file_id
          ? `Test upload succeeded. File ID: ${result.file_id}`
          : "Test upload succeeded."
      );
      await loadStatus();
    } catch (err) {
      setError(getErrorMessage(err, "Google Drive test upload failed."));
    } finally {
      setBusyAction("");
    }
  };

  const handleDisconnect = async () => {
    setBusyAction("disconnect");
    setError("");
    setNotice("");
    try {
      await disconnectGoogleDrive(token);
      setDisconnectOpen(false);
      await loadStatus();
      setNotice("Google Drive disconnected.");
    } catch (err) {
      setError(getErrorMessage(err, "Unable to disconnect Google Drive."));
    } finally {
      setBusyAction("");
    }
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  const connected = Boolean(statusData?.connected);
  const enabled = Boolean(statusData?.enabled);
  const isBusy = Boolean(busyAction);

  return (
    <div className="onlineBookingShell">
      <div className="onlineBookingWide online-booking-wide online-booking-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Google Drive Storage
            </h1>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            {connectedFromQuery && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                Google Drive connected successfully.
              </div>
            )}
            {callbackErrorFromQuery && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                Google Drive connection failed: {callbackErrorFromQuery}
              </div>
            )}

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            {notice && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {notice}
              </div>
            )}

            <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-semibold text-slate-900">Connection Status</h2>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    connected
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-slate-200 text-slate-700"
                  }`}
                >
                  {connected ? "Connected" : "Disconnected"}
                </span>
              </div>
              <p className="text-sm text-slate-700">
                Connect Google Drive to store portal branding files, client appointment
                photo attachments, and generated reports outside local server disk.
              </p>
              {statusData?.drive_account_email && (
                <p className="text-sm text-slate-600">
                  Connected account: <span className="font-medium">{statusData.drive_account_email}</span>
                </p>
              )}
              <p className="text-xs text-slate-500">
                Salon: {user?.salon_name || user?.salon_id || "Current salon"}
              </p>
            </section>

            <section className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {!connected ? (
                  <button
                    type="button"
                    onClick={handleConnect}
                    disabled={isBusy || loading}
                    className="rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                  >
                    {busyAction === "connect" ? "Connecting..." : "Connect Google Drive"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleToggleEnabled(!enabled)}
                    disabled={isBusy || loading}
                    className={`rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${
                      enabled ? "bg-slate-700 hover:bg-slate-800" : "bg-emerald-600 hover:bg-emerald-700"
                    }`}
                  >
                    {busyAction === "toggle"
                      ? "Saving..."
                      : enabled
                      ? "Disable Drive Storage"
                      : "Enable Drive Storage"}
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleTestUpload}
                  disabled={!connected || isBusy || loading}
                  className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                >
                  {busyAction === "test" ? "Testing..." : "Test Upload"}
                </button>

                <button
                  type="button"
                  onClick={() => setDisconnectOpen(true)}
                  disabled={!connected || isBusy || loading}
                  className="rounded-full border border-rose-300 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                >
                  Disconnect
                </button>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 p-4">
              <h3 className="text-base font-semibold text-slate-900 mb-2">
                What gets stored in Google Drive
              </h3>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                <li>Portal logo and background images</li>
                <li>Client portal nail design photo attachments</li>
                <li>Generated report files (CSV, PDF, HTML)</li>
              </ul>
            </section>

            {loading && (
              <div className="text-sm text-slate-500">Loading Google Drive integration status...</div>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        open={disconnectOpen}
        title="Disconnect Google Drive?"
        message="This only disconnects Ellyanna from Google Drive and disables Drive storage. Existing files already in Drive are not deleted."
        confirmText={busyAction === "disconnect" ? "Disconnecting..." : "Disconnect"}
        cancelText="Cancel"
        onClose={() => {
          if (busyAction !== "disconnect") {
            setDisconnectOpen(false);
          }
        }}
        onConfirm={handleDisconnect}
        isBusy={busyAction === "disconnect"}
      />
    </div>
  );
}
